import { useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import * as yup from "yup";

import InputGlobal from "shared/components/Form/InputGlobal";
import { Textarea } from "shared/components/Form/Textarea";
import { ModalFooter, ModalText } from "shared/components/Modals/Modal";
import SpinLoader, {
  LOADER_COLOR_DARK,
  LOADER_SIZE_LARGE,
  POSITION_ABSOLUTE,
} from "shared/components/SpinLoader";

import { deactivateProfileUser } from "shared/data/settings";

import { simplyClick } from "shared/helpers/analytics";

import styles from "@platformAssets/components/DeactivateModal.module.scss";
import stylesModal from "@platformAssets/components/Modal.module.scss";

import { ReactComponent as EyeIcon } from "assets/shared/img/icons/eye.svg";
import { ReactComponent as EyeIconHide } from "assets/shared/img/icons/eye-crossed.svg";

const MAX_LENGTH_PASSWORD = 255;
const MAX_LENGTH_COMMENT = 500;

const DeactivateStepTwo = ({
  handleCloseClick,
  setShowState,
  isDeleted,
  onDeletedSuccess,
  username,
}) => {
  const { t } = useTranslation("settings");

  const [password, setPassword] = useState("");
  const [errorPassword, setErrorPassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [comment, setComment] = useState("");
  const [errorComment, setErrorComment] = useState(null);

  const [isSending, setIsSending] = useState(false);

  const handlePasswordChange = (event) => {
    setPassword(event?.target?.value ?? "");
    setErrorPassword(null);
  };

  const handleCommentChange = (comment) => {
    setComment(comment);
    setErrorComment(null);
  };

  /**
   * Handle confirm click event
   * @param {Object} event Click event object
   */
  const handleConfirmClick = (event) => {
    event?.preventDefault();

    setIsSending(false);
    setShowState(false);
    handleCloseClick();
    window.location.href = `${import.meta.env.VITE_APP_LOGOUT_URL}?backUrl=/`;
  };

  const confirm = () => {
    onDeletedSuccess?.();
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  /**
   * Handle delete click event
   * @param {Object} event Click event object
   */
  const handleDeleteClick = (event) => {
    event?.preventDefault();

    const trimmedComment = comment.trim();
    const trimmedPassword = password.trim();

    if (isSending) {
      return;
    }

    setIsSending(true);

    const requestSchema = yup.object().shape({
      password: yup
        .string(t("common:error.field.empty"))
        .required(t("common:error.field.empty"))
        .min(5, t("deactivatePasswordMin"))
        .max(MAX_LENGTH_PASSWORD, t("deactivatePasswordMax")),

      comment: yup
        .string(t("common:error.comment.empty"))
        .required(t("common:error.comment.empty"))
        .max(MAX_LENGTH_COMMENT, t("deactivateCommentMax")),
    });

    requestSchema
      .validate(
        { password: trimmedPassword, comment: trimmedComment },
        { abortEarly: false }
      )
      .then(() => {
        deactivateProfileUser(
          { password: trimmedPassword, comment: trimmedComment },
          confirm,
          (error) => {
            setIsSending(false);

            setErrorPassword(
              error?.message?.length > 0 ? error?.message : error.toString()
            );
          }
        );
      })
      .catch((errors) => {
        console.log("catch-errorPassword", errors);

        const validationErrors = {};

        errors?.inner?.forEach((error) => {
          if (error.path && !validationErrors[error.path]) {
            validationErrors[error.path] = error.errors[0];
          }
        });

        setErrorPassword(validationErrors?.password ?? null);
        setErrorComment(validationErrors?.comment ?? null);

        setIsSending(false);
      });

    simplyClick(window.simplyEvent.cl_deactiv_prof_approve);
  };

  const isSubmitDisabled = password.length <= 0 || comment.length <= 0;

  if (isDeleted) {
    return (
      <>
        <ModalText
          customClass={styles.confirmationTitle}
          text={t("deactivateModal.confirmation.title")}
          isTitle
        />

        <ModalText
          text={t("deactivateModal.confirmation.textOne", { username })}
          customClass={styles.confirmationText}
        />
        <ModalText text={t("deactivateModal.confirmation.textTwo")} />

        <ModalFooter customClass={stylesModal.DeactivateFooter}>
          <div className={styles.buttonWrapper}>
            <button
              onClick={handleConfirmClick}
              className={stylesModal.primaryButton}
            >
              {t("deactivateModal.confirmation.button")}
            </button>
          </div>
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      <p className={styles.deactivateTitle}>
        {t("deactivateModalStepTwoTitle")}
      </p>
      <InputGlobal
        animatePlaceholder
        className={cn(styles.passwordInput, "deactivate-password")}
        error={Boolean(errorPassword)}
        errorText={errorPassword}
        handleChange={handlePasswordChange}
        name="password"
        type={showPassword ? "text" : "password"}
        rightIconSrc={showPassword ? <EyeIcon /> : <EyeIconHide />}
        handleClickRightIcon={toggleShowPassword}
        placeholder={t("deactivateModalPasswordPlaceholder")}
        value={password}
      />

      <div className={styles.textArea}>
        <Textarea
          customClass={errorComment ? "error_textarea" : ""}
          error={Boolean(errorComment)}
          handleChange={handleCommentChange}
          name="comment"
          placeholder={t("deactivateModalCommentPlaceholder")}
          rows={3}
          value={comment}
        />

        <div className={styles.textAreaText}>
          <p className={styles.errorText}>{errorComment}</p>
          <p
            className={`${styles.errorMaxLength} ${
              comment.trim().length > MAX_LENGTH_COMMENT ? styles.errorText : ""
            }`}
          >
            {comment.trim().length}/{MAX_LENGTH_COMMENT}
          </p>
        </div>
      </div>

      <ModalFooter customClass={stylesModal.DeactivateFooter}>
        <button
          onClick={handleDeleteClick}
          className={`${stylesModal.primaryButtonDeactivate} ${
            isSubmitDisabled ? stylesModal.disabled : ""
          }`}
        >
          {t("deactivateModalDelete")}
        </button>

        <button
          onClick={(event) => {
            handleCloseClick(event);
            simplyClick(window.simplyEvent.cl_deactiv_prof_cancel);
          }}
          className={stylesModal.defaultButton}
        >
          {t("cancelButton")}
        </button>
      </ModalFooter>

      {isSending && (
        <SpinLoader
          color={LOADER_COLOR_DARK}
          size={LOADER_SIZE_LARGE}
          position={POSITION_ABSOLUTE}
          withBlur={true}
        />
      )}
    </>
  );
};

export { DeactivateStepTwo };
